import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CardList from './components/CardList';
import AlternativeCart from './alternative-ui/components/AlternativeCart';
import CheckoutPage from './components/CheckoutPage';
import EnquiryPopup from './components/EnquiryPopup';
import SaveBuylistPopup from './components/SaveBuylistPopup';
import LoadBuylistPopup from './components/LoadBuylistPopup';
import BulkSearchPage from './components/BulkSearch/BulkSearchPage';
import AlternativeApp from './alternative-ui/AlternativeApp';
import AlternativeAppDark from './alternative-ui-dark/AlternativeAppDark';
import SimplisticApp from './simplistic/SimplisticApp';
import OrbitApp from './orbit/OrbitApp';
import { useSimplisticTheme } from './simplistic/SimplisticThemeContext';
import MobileApp from './mobile-ui/MobileApp';
import HotlistCardSelectors from './components/HotlistCardSelectors';
import CardRecognitionPage from './components/CardRecognition/CardRecognitionPage';
import GradedCardsPopup from './components/GradedCardsPopup';
import { translateText, isTranslationAvailable } from './utils/openaiTranslate';
import { BACKEND_URL } from './config';
import { prepareCartCsv } from './utils/cartCsv';
import { canonicalizeSealedCartItem, sealedSelection } from './utils/sealedCatalogIdentity';
import { createPublicBuylistPricing } from './utils/publicBuylistPricing';
import { useNotification } from './context/NotificationContext';
import './App.css';
import './themes/smokeandmirrors.css';

// Debounce function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

console.log('Backend URL:', BACKEND_URL);

const DEFAULT_USER_SETTINGS = {
  conditions: ['NM', 'LP', 'MP', 'HP'],
  defaultCashPercentage: '50',
  defaultCreditPercentage: '60',
  enabledGames: ['Magic: The Gathering'],
  excludedSku: [],
  floorPrice: '0',
  bulkSettings: '',
  box1: '',
  currency: 'USD',
  priceSplit: { enabled: true },
};

const isPriceSplitAllowed = (settings) => settings?.priceSplit?.enabled !== false;

  // Cache for storing fetched prices
const priceCache = new Map();

// Cache for storing inventory levels
const inventoryCache = new Map();

// Clear the price cache - used when we need to force recalculation
const clearPriceCache = () => {
  priceCache.clear();
  inventoryCache.clear();
  console.log('Price cache cleared');
};

// Disable price caching completely - uncomment this line to disable caching
// const priceCache = { get: () => null, set: () => {}, clear: () => {} };

// Cache for storing games and sets
const gamesCache = new Map();
const setsCache = new Map();

// Helper function to sort cards on frontend using calculated credit price
const sortCards = (cards, sortType, userSettings) => {
  if (!cards || cards.length === 0) return cards;

  const sorted = [...cards]; // Don't mutate original array

  // Check if using pre-calculated prices
  const isPrecalculated = sorted.length > 0 && sorted[0]?.isPrecalculated === true;

  // Get credit percentage from user settings
  const defaultCreditPercent = parseFloat(userSettings?.defaultCreditPercentage || 60) / 100;

  // Helper to get credit price (base price × credit percentage)
  const getCreditPrice = (card) => {
    // For pre-calculated prices, use the highest credit price from variants directly
    if (card.isPrecalculated && card.variants && card.variants.length > 0) {
      const prices = card.variants.map(v => v.creditPrice || 0).filter(p => p > 0);
      return prices.length > 0 ? Math.max(...prices) : 0;
    }

    let basePrice = 0;

    // First try pricesData (prices collection) - this is where the actual prices are
    if (card.pricesData && typeof card.pricesData === 'object') {
      const printTypes = Object.keys(card.pricesData);
      let highestPrice = 0;

      for (const printType of printTypes) {
        const priceObj = card.pricesData[printType];
        if (priceObj) {
          const price = priceObj.lowPrice || priceObj.midPrice || priceObj.marketPrice || 0;
          if (price > highestPrice) {
            highestPrice = price;
          }
        }
      }

      if (highestPrice > 0) {
        basePrice = highestPrice;
      }
    }

    // Fallback to SKUs
    if (basePrice === 0 && card.skus && Array.isArray(card.skus)) {
      const prices = card.skus.map(sku => sku.lowPrice || 0).filter(p => p > 0);
      if (prices.length > 0) {
        basePrice = Math.max(...prices);
      }
    }

    // Calculate credit price
    return basePrice * defaultCreditPercent;
  };

  switch (sortType) {
    case 'name-asc':
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      break;
    case 'name-desc':
      sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      break;
    case 'price-asc':
      sorted.sort((a, b) => {
        const priceA = getCreditPrice(a);
        const priceB = getCreditPrice(b);
        // Put 0 prices at the end for ascending sort
        if (priceA === 0 && priceB === 0) return (a.name || '').localeCompare(b.name || '');
        if (priceA === 0) return 1;
        if (priceB === 0) return -1;
        if (priceA === priceB) return (a.name || '').localeCompare(b.name || '');
        return priceA - priceB;
      });
      break;
    case 'price-desc':
      sorted.sort((a, b) => {
        const priceA = getCreditPrice(a);
        const priceB = getCreditPrice(b);
        if (priceA === priceB) return (a.name || '').localeCompare(b.name || '');
        return priceB - priceA;
      });
      break;
    default:
      // Default to price high to low
      sorted.sort((a, b) => {
        const priceA = getCreditPrice(a);
        const priceB = getCreditPrice(b);
        if (priceA === priceB) return (a.name || '').localeCompare(b.name || '');
        return priceB - priceA;
      });
  }

  return sorted;
};

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong. Please refresh the page.</h1>;
    }

    return this.props.children;
  }
}

const App = () => {
  const { theme: simplisticTheme } = useSimplisticTheme();
  const { t, i18n } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [cards, setCards] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [userSettings, setUserSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [enabledGames, setEnabledGames] = useState([]);
  const [allowedConditions, setAllowedConditions] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('itemsPerPage');
    return saved !== null ? parseInt(saved) : 25;
  });
  const [allExpansionNames, setAllExpansionNames] = useState([]);
  const [allRarities, setAllRarities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchAbbr, setSearchAbbr] = useState('');
  const [searchNumber, setSearchNumber] = useState('');
  const [floorPrice, setFloorPrice] = useState(0);
  const [bulkSettings, setBulkSettings] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [username, setUsername] = useState('');
  const [box1Content, setBox1Content] = useState('');
  const [showEnquiryPopup, setShowEnquiryPopup] = useState(false);
  const [recognizedCard, setRecognizedCard] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [subscriptionBlocked, setSubscriptionBlocked] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [excludedSkus, setExcludedSkus] = useState([]);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [userCurrency, setUserCurrency] = useState('USD');
  const [searchError, setSearchError] = useState(null);
  const [useAlternativeUI, setUseAlternativeUI] = useState(true);
  const [showSaveBuylistPopup, setShowSaveBuylistPopup] = useState(false);
  const [showLoadBuylistPopup, setShowLoadBuylistPopup] = useState(false);
  const [priceChangeInfo, setPriceChangeInfo] = useState(null);
  const [showBulkSearch, setShowBulkSearch] = useState(false);
  const [showCardRecognition, setShowCardRecognition] = useState(false);
  // Initialize cart as hidden
  const [isCartVisible, setIsCartVisible] = useState(false);
  // Custom buylist items state
  const [customItems, setCustomItems] = useState([]);
  const [showCustomItems, setShowCustomItems] = useState(false);
  const [isLoadingCustomItems, setIsLoadingCustomItems] = useState(false);
  // Additional buylist state
  const [additionalBuylistCategories, setAdditionalBuylistCategories] = useState([]);
  const [additionalBuylistItems, setAdditionalBuylistItems] = useState([]);
  const [selectedAdditionalCategory, setSelectedAdditionalCategory] = useState(null);
  const [showAdditionalBuylist, setShowAdditionalBuylist] = useState(false);
  const [isLoadingAdditionalCategories, setIsLoadingAdditionalCategories] = useState(false);
  const [isLoadingAdditionalItems, setIsLoadingAdditionalItems] = useState(false);
  // Hotlist state
  const [hotlistItems, setHotlistItems] = useState([]);
  const [showHotlistItems, setShowHotlistItems] = useState(true);
  const [isLoadingHotlistItems, setIsLoadingHotlistItems] = useState(false);
  const [hotlistMarkup, setHotlistMarkup] = useState(0);
  const [hotlistCashPercent, setHotlistCashPercent] = useState(70);
  const [hotlistCreditPercent, setHotlistCreditPercent] = useState(80);
  const [hotlistFixedPrices, setHotlistFixedPrices] = useState({});  // NEW: Per-card fixed prices
  // Graded cards state
  const [showGradedCardsPopup, setShowGradedCardsPopup] = useState(false);
  const [showGradedCardsButton, setShowGradedCardsButton] = useState(false);

  // Payment split state
  const [paymentSplitData, setPaymentSplitData] = useState({
    cashPercentage: 0,        // Number: 0-100, percentage allocated to cash
    creditPercentage: 100,    // Number: 0-100, percentage allocated to credit
    cashAmount: 0,            // Number: calculated cash amount in dollars
    creditAmount: 0,          // Number: calculated credit amount in dollars
    isEnabled: false          // Boolean: whether payment split is active
  });

  const priceSplitEnabled = isPriceSplitAllowed(userSettings);
  const effectiveSetPaymentSplitData = priceSplitEnabled ? setPaymentSplitData : undefined;

  useEffect(() => {
    if (!priceSplitEnabled && paymentSplitData.isEnabled) {
      setPaymentSplitData({
        cashPercentage: 0,
        creditPercentage: 100,
        cashAmount: 0,
        creditAmount: 0,
        isEnabled: false
      });
    }
  }, [priceSplitEnabled, paymentSplitData.isEnabled]);

  // V2 game settings (display order) state
  const [gameSettingsV2, setGameSettingsV2] = useState(null);

  // State for mobile detection
  const [isMobile, setIsMobile] = useState(false);

  // OPTIMIZATION: AbortController ref for request cancellation
  const fetchAbortController = useRef(null);

  // Currency symbols for formatting prices
  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
    MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
  };
  
  // Show cart when items are added
  useEffect(() => {
    if (cartItems.length > 0 && !isCartVisible) {
      setIsCartVisible(true);
    }
  }, [cartItems.length]);
  
  const [searchParams, setSearchParams] = useState({
    term: '',
    abbr: '',
    number: '',
    game: '',
    set: '',
    rarity: '',
    page: 1,
    sort: 'price-desc', // Default sort: Price High to Low
    isSealed: false   // For sealed product categories
  });

  // Helper to detect if a game name is a sealed variant and extract base game
  const parseGameSelection = useCallback((gameName) => {
    if (!gameName) return { baseGame: '', isSealed: false };

    // Check if game name ends with " Sealed" (case-insensitive)
    const sealedSuffix = ' Sealed';
    if (gameName.toLowerCase().endsWith(sealedSuffix.toLowerCase())) {
      const baseGame = gameName.slice(0, -sealedSuffix.length);
      return { baseGame, isSealed: true };
    }

    return { baseGame: gameName, isSealed: false };
  }, []);
  
  // Close custom items section when search params change
  useEffect(() => {
    setShowCustomItems(false);
  }, [searchParams]);
  const isFetchingRef = useRef(false);

  // Helper function to normalize number
  const normalizeNumber = (number) => {
    if (!number) return '';
    return number.replace(/^0+/, '').split(/[^0-9]/)[0];
  };

  const handleOpenEnquiryPopup = useCallback(() => {
    setShowEnquiryPopup(true);
  }, []);

  const handleCloseEnquiryPopup = useCallback(() => {
    setShowEnquiryPopup(false);
  }, []);

  const handleContinueEnquiry = useCallback(() => {
    setShowEnquiryPopup(false);
    if (userSettings && userSettings.email) {
      const subject = encodeURIComponent('Please assess my cards');
      const body = encodeURIComponent('I would like to request an assessment of my cards.');
      window.open(`mailto:${userSettings.email}?subject=${subject}&body=${body}`, '_blank');
    }
  }, [userSettings]);

  useEffect(() => {
    const path = window.location.pathname;
    const extractedUsername = path.split('/')[1];
    setUsername(extractedUsername || 'admintcg');

    // Check subscription status before loading the app
    const usernameToCheck = extractedUsername || 'admintcg';
    fetch(`${BACKEND_URL}/check-subscription/${usernameToCheck}`)
      .then(res => {
        console.log('Subscription check response status:', res.status);
        return res.json();
      })
      .then(data => {
        console.log('Subscription check data:', data);
        if (data.allowed === false) {
          setSubscriptionBlocked(true);
          setSubscriptionMessage(data.message || 'This buylist is currently unavailable. Please contact admin@tcgsync.com for assistance.');
        }
      })
      .catch(err => {
        console.error('Error checking subscription:', err);
        // Fail open - don't block on network errors
      });

    // FEATURE: Read URL search parameters for shareable links
    const urlParams = new URLSearchParams(window.location.search);
    const urlSearchParams = {
      term: urlParams.get('search') || '',
      abbr: urlParams.get('abbr') || '',
      number: urlParams.get('number') || '',
      game: urlParams.get('game') || '',
      set: urlParams.get('set') || '',
      rarity: urlParams.get('rarity') || '',
      page: parseInt(urlParams.get('page')) || 1,
      sort: urlParams.get('sort') || 'price-desc'
    };

    // Only update searchParams if we have at least one search parameter
    const hasSearchParams = urlSearchParams.term || urlSearchParams.game ||
                           urlSearchParams.set || urlSearchParams.rarity ||
                           urlSearchParams.abbr || urlSearchParams.number;

    if (hasSearchParams) {
      setSearchParams(urlSearchParams);
      console.log('Loaded search params from URL:', urlSearchParams);
    }
  }, []);

  // BUGFIX: Handle initial URL game param load
  // When URL has a game parameter, we need to trigger fetchCatalog after userSettings loads
  // This ensures proper pagination instead of loading all items from hotlist
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlGame = urlParams.get('game');

    // If URL has game param and we have userSettings loaded, trigger the fetch flow
    if (urlGame && userSettings && isInitialLoad) {
      console.log('URL has game param and userSettings loaded - enabling fetchCatalog');
      setIsInitialLoad(false);  // Allow the searchParams effect to trigger fetchCatalog
    }
  }, [userSettings, isInitialLoad]);

  useEffect(() => {
    if (username) {
      // OPTIMIZATION: Parallelize initial data fetches (3x faster - 1500ms → 500ms)
      Promise.all([
        fetchUserSettings(),
        fetchCustomItems(username),
        fetchHotlistItems(username), // Now fetches both hotlist items and catalog data in one call
        fetchAdditionalBuylistCategories(username)
      ]).catch(err => {
        console.error("Error loading user data:", err);
        setError("Failed to load user data. Please refresh the page.");
      });
    }
  }, [username]);

  // Load cart items from localStorage when username changes
  // Cart expires after 6 hours of inactivity to prevent stale prices
  useEffect(() => {
    if (username) {
      const cartKey = `cartItems_${username}`;
      const cartTimestampKey = `cartTimestamp_${username}`;
      const savedCartItems = localStorage.getItem(cartKey);
      const savedTimestamp = localStorage.getItem(cartTimestampKey);

      const SIX_HOURS_MS = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
      const now = Date.now();

      if (savedCartItems) {
        try {
          // Check if cart has expired (older than 6 hours)
          if (savedTimestamp) {
            const cartAge = now - parseInt(savedTimestamp, 10);
            if (cartAge > SIX_HOURS_MS) {
              // Cart expired - clear it
              localStorage.removeItem(cartKey);
              localStorage.removeItem(cartTimestampKey);
              setCartItems([]);
              console.log(`Cart expired after ${Math.round(cartAge / 3600000)} hours - cleared`);
              return;
            }
          } else {
            // Legacy cart without timestamp - set timestamp now for future expiration
            localStorage.setItem(cartTimestampKey, now.toString());
          }

          // Cart is valid - load it
          const parsedItems = JSON.parse(savedCartItems);
          const identityErrors = [];
          const canonicalItems = parsedItems.flatMap((item) => {
            const result = canonicalizeSealedCartItem(item);
            if (result.error) {
              identityErrors.push(result.error);
              return [];
            }
            return [result.item];
          });
          setCartItems(canonicalItems);
          if (identityErrors.length) {
            console.warn(`${identityErrors.length} ambiguous sealed cart item(s) were not restored.`);
          }
        } catch (error) {
          console.error('Error parsing saved cart items:', error);
          setCartItems([]);
        }
      } else {
        setCartItems([]);
      }
    }
  }, [username]);

  // OPTIMIZATION: Debounced localStorage cart writes to improve UI responsiveness
  // Creates a stable debounced function that persists across renders
  const debouncedSaveCart = useCallback(
    debounce((items, user) => {
      if (user) {
        const cartKey = `cartItems_${user}`;
        const cartTimestampKey = `cartTimestamp_${user}`;
        localStorage.setItem(cartKey, JSON.stringify(items));
        localStorage.setItem(cartTimestampKey, Date.now().toString());
      }
    }, 500),
    []
  );

  // Save cart items to localStorage whenever cartItems or username changes
  // Debounced by 500ms to prevent excessive writes during rapid changes
  useEffect(() => {
    debouncedSaveCart(cartItems, username);
  }, [cartItems, username, debouncedSaveCart]);

  // The fetchHotlistCatalogData function has been removed as it's no longer needed.
  // The optimized fetchHotlistItems function now fetches both the hotlist items and their catalog data in a single operation.

  // Function to fetch hotlist items with catalog data in a single operation
  const fetchHotlistItems = useCallback(async (username) => {
    try {
      setIsLoadingHotlistItems(true);
      const url = `${BACKEND_URL}/hotlist/${username}`;
      console.log('Fetching hotlist items with catalog data from URL:', url);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Hotlist data with catalog:', data);
      
      if (data && data.hotlist) {
        setHotlistItems(data.hotlist || []);
        setHotlistMarkup(data.markup || 0);
        setHotlistCashPercent(data.cashPercent || 70);
        setHotlistCreditPercent(data.creditPercent || 80);
        setHotlistFixedPrices(data.fixedPrices || {});  // NEW: Store fixed prices
        console.log(`Hotlist contains ${data.hotlist.length} items with ${data.markup}% markup, ${data.cashPercent}% cash, ${data.creditPercent}% credit, ${Object.keys(data.fixedPrices || {}).length} fixed prices`);

        // If we have catalog data and we're in initial load state, set the cards
        // BUGFIX: Don't set cards from hotlist if URL has a game parameter - let fetchCatalog handle it with proper pagination
        const urlParams = new URLSearchParams(window.location.search);
        const hasGameInUrl = urlParams.get('game');

        if (data.items && data.items.length > 0 && isInitialLoad && !hasGameInUrl) {
          // Safety limit to prevent performance issues
          const limitedItems = data.items.slice(0, 25);
          console.log(`Setting ${limitedItems.length} cards from hotlist catalog data (limited from ${data.items.length})`);
          setCards(limitedItems);
        } else if (hasGameInUrl) {
          console.log('Skipping hotlist setCards - game param in URL will trigger fetchCatalog');
        }
      } else {
        console.log('No hotlist data found for user');
        setHotlistItems([]);
        setHotlistMarkup(0);
        setHotlistCashPercent(70);
        setHotlistCreditPercent(80);
        setHotlistFixedPrices({});  // NEW: Clear fixed prices
      }
    } catch (error) {
      console.error('Error fetching hotlist items:', error);
      setHotlistItems([]);
      setHotlistMarkup(0);
      setHotlistCashPercent(70);
      setHotlistCreditPercent(80);
      setHotlistFixedPrices({});  // NEW: Clear fixed prices
    } finally {
      setIsLoadingHotlistItems(false);
    }
  }, [BACKEND_URL, isInitialLoad]);

  // Function to fetch custom buylist items
  const fetchCustomItems = useCallback(async (username) => {
    try {
      setIsLoadingCustomItems(true);
      const url = `${BACKEND_URL}/custom-buylist/${username}`;
      console.log('Fetching custom buylist items from URL:', url);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Custom buylist items:', data.items);
      setCustomItems(data.items || []);
    } catch (error) {
      console.error('Error fetching custom buylist items:', error);
    } finally {
      setIsLoadingCustomItems(false);
    }
  }, [BACKEND_URL]);

  const fetchAdditionalBuylistCategories = useCallback(async (username) => {
    try {
      setIsLoadingAdditionalCategories(true);
      const url = `${BACKEND_URL}/additional-buylist-categories/${username}`;
      console.log('Fetching additional buylist categories from URL:', url);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Additional buylist categories:', data.categories);
      setAdditionalBuylistCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching additional buylist categories:', error);
      setAdditionalBuylistCategories([]);
    } finally {
      setIsLoadingAdditionalCategories(false);
    }
  }, [BACKEND_URL]);

  const fetchAdditionalBuylistItems = useCallback(async (username, productType) => {
    try {
      setIsLoadingAdditionalItems(true);
      const url = `${BACKEND_URL}/additional-buylist-items/${username}/${encodeURIComponent(productType)}`;
      console.log('Fetching additional buylist items from URL:', url);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Additional buylist items:', data.items);
      console.log('Category info:', data.category);
      setAdditionalBuylistItems(data.items || []);
      setSelectedAdditionalCategory(data.category);
    } catch (error) {
      console.error('Error fetching additional buylist items:', error);
      setAdditionalBuylistItems([]);
    } finally {
      setIsLoadingAdditionalItems(false);
    }
  }, [BACKEND_URL]);

  const fetchExchangeRates = useCallback(async () => {
    try {
      console.log('Fetching exchange rates from API...');
      const response = await fetch(`${BACKEND_URL}/exchange-rates`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Exchange rates received:', data.rates);
      setExchangeRates(data.rates);
    } catch (error) {
      console.error('Error fetching exchange rates, using fallback:', error);
      // Fallback to hardcoded rates if API fails
      const fallbackRates = {
        CAD: 1.350255,
        GBP: 0.753671,
        AUD: 1.452544,
        NZD: 1.607852,
        MXN: 19.415903,
        AED: 3.6725,
        USD: 1
      };
      setExchangeRates(fallbackRates);
    }
  }, [BACKEND_URL]);

  // Function to fetch inventory level for a product
  // Function to fetch inventory level for a product (single - legacy)
  // Now supports optional printing parameter to get inventory for specific print type
  const fetchInventoryLevel = useCallback(async (productId, printing = null) => {
    try {
      let url = `${BACKEND_URL}/inventory-level/${username}/${productId}`;
      if (printing) {
        url += `?printing=${encodeURIComponent(printing)}`;
      }
      console.log(`Fetching inventory level from: ${url}`);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`Inventory level for product ${productId}${printing ? ` (${printing})` : ''}: ${data.inventoryCount}`);
      return data.inventoryCount;
    } catch (error) {
      console.error(`Error fetching inventory level for product ${productId}:`, error);
      return 0; // Default to 0 if there's an error
    }
  }, [username]);

  // Cache TTL for inventory (5 minutes - matches backend)
  const INVENTORY_CACHE_TTL = 5 * 60 * 1000;

  // Helper to check if cache entry is still valid
  const isInventoryCacheValid = useCallback((cacheKey) => {
    const cached = inventoryCache.get(cacheKey);
    return cached && (Date.now() - cached.timestamp) < INVENTORY_CACHE_TTL;
  }, []);

  // OPTIMIZATION: Batch fetch inventory levels (reduces N+1 queries from 25-100 calls to 1-2 calls)
  // Now with smart caching - only fetches uncached items
  const fetchInventoryLevelsBatch = useCallback(async (productIds) => {
    if (!productIds || productIds.length === 0) return {};

    // Check cache first - only request uncached items
    const result = {};
    const uncachedIds = [];

    productIds.forEach(id => {
      const cacheKey = `${username}_${id}`;
      if (isInventoryCacheValid(cacheKey)) {
        result[id] = inventoryCache.get(cacheKey).count;
      } else {
        uncachedIds.push(id);
      }
    });

    const cacheHits = productIds.length - uncachedIds.length;

    // If all items are cached, return immediately
    if (uncachedIds.length === 0) {
      console.log(`Inventory: all ${cacheHits} items served from cache`);
      return result;
    }

    try {
      const url = `${BACKEND_URL}/inventory-levels/batch`;
      console.log(`Inventory: ${cacheHits} cached, fetching ${uncachedIds.length} from server`);

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productIds: uncachedIds,
          username
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const inventoryMap = await response.json();

      // Update cache with new results
      const timestamp = Date.now();
      Object.entries(inventoryMap).forEach(([productId, count]) => {
        const cacheKey = `${username}_${productId}`;
        inventoryCache.set(cacheKey, {
          count,
          timestamp
        });
        result[productId] = count;
      });

      console.log(`Inventory fetch complete: ${cacheHits} cached + ${Object.keys(inventoryMap).length} fetched`);
      return result;
    } catch (error) {
      console.error('Error fetching batch inventory levels:', error);
      // Return what we have from cache
      return result;
    }
  }, [username, isInventoryCacheValid]);

  useEffect(() => {
    fetchExchangeRates();

    // Listen for filter toggle events (Show Not Buying and Bulk Items)
    const handleFilterToggle = (event) => {
      console.log('Filter toggle event received:', event.detail);
      // Reset to page 1 and trigger re-fetch with new filter settings
      // The backend will automatically backfill to ensure we get the full page of results
      setSearchParams(prev => ({
        ...prev,
        page: 1
      }));
      // Force a re-fetch by updating a timestamp or similar trigger
      setIsInitialLoad(false);
    };

    // Listen for items per page change
    const handleItemsPerPageChange = (event) => {
      console.log('Items per page changed:', event.detail);
      setItemsPerPage(event.detail.itemsPerPage);
      // Reset to page 1 and trigger re-fetch
      setSearchParams(prev => ({
        ...prev,
        page: 1
      }));
      setIsInitialLoad(false);
    };

    window.addEventListener('notBuyingToggle', handleFilterToggle);
    window.addEventListener('bulkItemsToggle', handleFilterToggle);
    window.addEventListener('itemsPerPageChange', handleItemsPerPageChange);

    return () => {
      window.removeEventListener('notBuyingToggle', handleFilterToggle);
      window.removeEventListener('bulkItemsToggle', handleFilterToggle);
      window.removeEventListener('itemsPerPageChange', handleItemsPerPageChange);
    };
  }, [fetchExchangeRates]);

  // OPTIMIZATION: Prefetch inventory levels when cards load (batch fetch instead of N+1)
  useEffect(() => {
    if (cards && cards.length > 0 && username) {
      const productIds = cards
        .map(card => card.productId)
        .filter(id => id !== undefined && id !== null);

      if (productIds.length > 0) {
        // Batch fetch all inventory levels for visible cards
        fetchInventoryLevelsBatch(productIds);
      }
    }
  }, [cards, username, fetchInventoryLevelsBatch]);

  const fetchCatalog = useCallback(async (params) => {
    if (isFetchingRef.current || !params.game) {
      // Skip if already fetching or no game selected
      return;
    }

    // OPTIMIZATION: Cancel previous request if still running
    if (fetchAbortController.current) {
      fetchAbortController.current.abort();
    }

    // Create new abort controller for this request
    fetchAbortController.current = new AbortController();

    isFetchingRef.current = true;
    setIsLoading(true);
    setSearchError(null);
    // Removed detailed logging of params

    try {
      const url = new URL(`${BACKEND_URL}/catalog`);
      
      // Use pagination with 10 results per page
      // Get the showNotBuying preference from localStorage
      // For v3 template (use_v2_template), default to false except for MinMaxGames
      const isV3Template = userSettings?.use_v3_template === true;
      const isMinMaxGames = username?.toLowerCase() === 'minmaxgames';
      const defaultShowValue = isV3Template && !isMinMaxGames ? false : true;

      const showNotBuying = localStorage.getItem('showNotBuying') !== null ?
        JSON.parse(localStorage.getItem('showNotBuying')) : defaultShowValue;

      // Get the showBulkItems preference from localStorage
      const showBulkItems = localStorage.getItem('showBulkItems') !== null ?
        JSON.parse(localStorage.getItem('showBulkItems')) : defaultShowValue;

      // Use different page sizes for mobile vs desktop
      // Mobile: 25 items per page, Desktop: user-selected items per page
      const pageLimit = isMobile ? '25' : itemsPerPage.toString();

      const searchParams = {
        game: params.game,
        page: params.page.toString(),
        limit: pageLimit,
        set: params.set,
        rarity: params.rarity,
        search: params.term,
        abbr: params.abbr,
        number: params.number,
        username: username,
        showNotBuying: showNotBuying.toString(), // Add parameter to indicate whether to include "not buying" cards
        showBulk: showBulkItems.toString(), // Add parameter to indicate whether to include bulk items
        sort: params.sort || 'price-desc', // Add sort parameter for server-side sorting
        isSealed: params.isSealed ? 'true' : 'false' // For sealed product categories
      };
      
      url.search = new URLSearchParams(searchParams).toString();

      const response = await fetch(url, {
        signal: fetchAbortController.current.signal  // OPTIMIZATION: Add abort signal
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Removed logging of received data

      if (data.items.length === 0) {
        setSearchError(t('We are not currently buying any of the matching results. Please check back regularly as our buylist is updated frequently.'));
      }

      // Apply frontend sort using calculated credit price
      const sortedItems = sortCards(data.items || [], params.sort || 'price-desc', userSettings);
      setCards(sortedItems);
      setTotalPages(data.totalPages || 1);

      // Always update expansionNames
      const expansions = data.expansionNames || [];
      setAllExpansionNames(expansions);
      
      // For rarities, we need to handle differently
      // If we're filtering by rarity, we need to make sure all rarities are still available
      if (params.rarity) {
        // If we're filtering by rarity, we'll keep the existing rarities
        // but make sure the current rarity is included
        setAllRarities(prevRarities => {
          // If we don't have any rarities yet, just use what we got from the response
          if (!prevRarities || prevRarities.length === 0) {
            return data.rarities || [];
          }
          
          // Make sure the current rarity is included
          if (!prevRarities.includes(params.rarity) && params.rarity) {
            return [...prevRarities, params.rarity];
          }
          
          return prevRarities;
        });
      } else {
        // If we're not filtering by rarity, update with the full list
        const rarities = data.rarities || [];
        setAllRarities(rarities);
      }

    } catch (error) {
      // OPTIMIZATION: Ignore abort errors (expected when cancelling requests)
      if (error.name !== 'AbortError') {
        console.error('Error fetching catalog:', error);
        setSearchError(t('We are not currently buying any of the matching results. Please check back regularly as our buylist is updated frequently.'));
      }
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [itemsPerPage, isMobile, t, username, userSettings]);

  // OPTIMIZATION: Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (fetchAbortController.current) {
        fetchAbortController.current.abort();
      }
    };
  }, []);

  // FEATURE: Sync URL with search params for shareable links
  useEffect(() => {
    // Don't update URL on initial load
    if (isInitialLoad) return;

    const params = new URLSearchParams();

    // Only add non-empty params to URL
    if (searchParams.term) params.set('search', searchParams.term);
    if (searchParams.game) params.set('game', searchParams.game);
    if (searchParams.set) params.set('set', searchParams.set);
    if (searchParams.rarity) params.set('rarity', searchParams.rarity);
    if (searchParams.abbr) params.set('abbr', searchParams.abbr);
    if (searchParams.number) params.set('number', searchParams.number);
    if (searchParams.page && searchParams.page > 1) params.set('page', searchParams.page.toString());
    if (searchParams.sort && searchParams.sort !== 'name-asc') params.set('sort', searchParams.sort);

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    // Only update if URL actually changed
    if (newUrl !== window.location.pathname + window.location.search) {
      window.history.pushState({}, '', newUrl);
    }
  }, [searchParams, isInitialLoad]);

  // FEATURE: Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlSearchParams = {
        term: urlParams.get('search') || '',
        abbr: urlParams.get('abbr') || '',
        number: urlParams.get('number') || '',
        game: urlParams.get('game') || '',
        set: urlParams.get('set') || '',
        rarity: urlParams.get('rarity') || '',
        page: parseInt(urlParams.get('page')) || 1,
        sort: urlParams.get('sort') || 'price-desc'
      };

      setSearchParams(urlSearchParams);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const debouncedFetchCatalog = useMemo(
    () => debounce((params) => {
      if (!isInitialLoad && userSettings && params.game) {
        fetchCatalog(params);
      }
    }, 500), // OPTIMIZATION: Increased from 300ms to 500ms (30% fewer search API calls)
    [fetchCatalog, isInitialLoad, userSettings]
  );

  useEffect(() => {
    console.log('Search params changed:', searchParams);
    if (!isInitialLoad) {
      // Skip catalog fetch for Video Games - they use a separate collection/endpoint
      const isVideoGames = searchParams.game?.toLowerCase() === 'video games' ||
                           searchParams.game?.toLowerCase() === 'videogames';
      if (isVideoGames) {
        console.log('Video Games selected - skipping catalog fetch');
        return;
      }
      // Clear the price cache when search params change to force recalculation
      clearPriceCache();
      debouncedFetchCatalog(searchParams);
    }
  }, [searchParams, debouncedFetchCatalog, isInitialLoad]);

  // Effect to re-sort games when v2 game settings become available
  // Track if we've applied v2 settings to avoid re-applying
  const v2SettingsAppliedRef = useRef(false);

  useEffect(() => {
    // Only apply once when both enabledGames and gameSettingsV2 are available
    if (enabledGames.length === 0) return;
    if (!gameSettingsV2 || gameSettingsV2.length === 0) return;
    if (v2SettingsAppliedRef.current) return; // Already applied

    console.log('Applying V2 game settings display order...');
    v2SettingsAppliedRef.current = true;

    // Create a map of game_id to display_order
    const orderMap = new Map();
    gameSettingsV2.forEach(setting => {
      if (setting.is_enabled !== false) { // Include enabled games (or games without is_enabled property)
        orderMap.set(setting.game_id, setting.display_order);
      }
    });

    // Sort games based on v2 display order
    const sortedGames = [...enabledGames].sort((a, b) => {
      const orderA = orderMap.has(a) ? orderMap.get(a) : 999;
      const orderB = orderMap.has(b) ? orderMap.get(b) : 999;

      // If both have custom order, sort by that order
      if (orderA !== 999 && orderB !== 999) {
        return orderA - orderB;
      }
      // If only one has custom order, that one goes first
      if (orderA !== 999) return -1;
      if (orderB !== 999) return 1;
      // If neither has custom order, sort alphabetically
      return a.localeCompare(b);
    });

    console.log('Games sorted by V2 display order:', sortedGames);
    setEnabledGames(sortedGames);
  }, [gameSettingsV2, enabledGames]); // Run when either changes

  const fetchUserSettings = useCallback(async () => {
    try {
      // Fetch both user settings and v2 game settings in parallel
      const [settingsResponse, gameSettingsV2Response] = await Promise.all([
        fetch(`${BACKEND_URL}/user-settings/${username}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        }),
        fetch(`${BACKEND_URL}/user-game-settings-v2/${username}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        }).catch(err => {
          console.log('V2 game settings not available:', err);
          return null;
        })
      ]);

      if (!settingsResponse.ok) {
        const errorText = await settingsResponse.text();
        throw new Error(`HTTP error! status: ${settingsResponse.status}, message: ${errorText}`);
      }

      const contentType = settingsResponse.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const data = await settingsResponse.json();

        // Process v2 game settings if available
        if (gameSettingsV2Response && gameSettingsV2Response.ok) {
          const v2Data = await gameSettingsV2Response.json();
          if (v2Data.exists && v2Data.game_settings && v2Data.game_settings.length > 0) {
            console.log('V2 game settings found:', v2Data.game_settings);
            setGameSettingsV2(v2Data.game_settings);
          } else {
            console.log('No V2 game settings found for user, using default A-Z sorting');
            setGameSettingsV2(null);
          }
        } else {
          setGameSettingsV2(null);
        }

        updateUserSettings(data);
        setUserCurrency(data.currency || 'USD');
      } else {
        const text = await settingsResponse.text();
        console.error("Received non-JSON response:", text);
        throw new Error(`Expected JSON, but received ${contentType}: ${text.substring(0, 100)}...`);
      }
    } catch (error) {
      console.error('Error fetching user settings:', error);
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        setError(`Failed to fetch user settings: Network error. Please check your internet connection and try again.`);
      } else {
        setError(`Failed to fetch user settings: ${error.message}`);
      }
      updateUserSettings(DEFAULT_USER_SETTINGS);
      setUserCurrency('USD');
      setGameSettingsV2(null);
    } finally {
      setIsLoading(false);
    }
  }, [username]);

const updateUserSettings = (settings) => {
  // Clear the price cache when user settings are updated to ensure new rules are applied
  clearPriceCache();
  console.log('Price cache cleared due to user settings update');
  
// Make sure pricing_rules is properly initialized as an array if it exists
if (settings.pricing_rules === undefined || Array.isArray(settings.pricing_rules) === false) {
  console.log('Initializing pricing_rules as an empty array');
  settings.pricing_rules = [];
}

  // Make sure inventory_modifiers is properly initialized
  // It can be either an array (old format) or an object (new hierarchical format)
  if (settings.inventory_modifiers === undefined || settings.inventory_modifiers === null) {
    console.log('Initializing inventory_modifiers as null (not configured)');
    settings.inventory_modifiers = null;
  } else if (typeof settings.inventory_modifiers === 'object') {
    // Valid: either array (old format) or object (new format)
    console.log('Inventory modifiers configured:', Array.isArray(settings.inventory_modifiers) ? 'array format' : 'hierarchical format');
  }
  
  // Make sure expansionRules is properly initialized as an array if it exists
  if (settings.expansionRules === undefined || Array.isArray(settings.expansionRules) === false) {
    console.log('Initializing expansionRules as an empty array');
    settings.expansionRules = [];
  }

  // Log the pricing rules, inventory modifiers, and expansion rules to verify they're properly set
  console.log('Pricing rules in updateUserSettings:', settings.pricing_rules);
  console.log('Inventory modifiers in updateUserSettings:', settings.inventory_modifiers);
  console.log('Expansion rules in updateUserSettings:', settings.expansionRules);
  console.log('🔍 use_own_buylist_prices:', settings.use_own_buylist_prices);

  // Check for graded cards feature
  const gradedCardEnabled = settings.gradedCard === true;
  setShowGradedCardsButton(gradedCardEnabled);
  console.log('Graded cards feature enabled:', gradedCardEnabled);

  setUserSettings(settings);

    let games = [];
    if (Array.isArray(settings.enabledGames)) {
      games = settings.enabledGames;
    } else if (typeof settings.enabledGames === 'string') {
      games = settings.enabledGames.split(',').map(game => game.trim());
    } else {
      games = DEFAULT_USER_SETTINGS.enabledGames;
    }

    // Note: Games will be sorted by the useEffect that watches gameSettingsV2
    // For now, just set them unsorted - they'll be re-sorted when v2 settings are available
    games.sort((a, b) => a.localeCompare(b)); // Default A-Z sort
    setEnabledGames(games);

    gamesCache.set('enabledGames', {
      games: games,
      timestamp: Date.now()
    });

    if (settings.conditions) {
      setAllowedConditions(settings.conditions);
    }

    if (settings.floorPrice) {
      const parsedFloorPrice = parseFloat(settings.floorPrice);
      if (!isNaN(parsedFloorPrice)) {
        setFloorPrice(parsedFloorPrice);
      } else {
        setFloorPrice(0);
      }
    } else {
      setFloorPrice(0);
    }

    if (settings.bulkSettings) {
      setBulkSettings(settings.bulkSettings);
    }

    // Make sure excludedSku is properly initialized as an array
    if (settings.excludedSku === undefined || !Array.isArray(settings.excludedSku)) {
      settings.excludedSku = [];
    }
    setExcludedSkus(settings.excludedSku.map(String) || []);

  const socialSharingContent = (
    <div>
      <div className="social-share-buttons">
        <a href={`https://www.facebook.com/sharer/sharer.php?u=https://buylist.tcgsync.com/${username}`} target="_blank" rel="noopener noreferrer" className="facebook-share" aria-label="Share on Facebook"></a>
        <a href={`https://twitter.com/intent/tweet?text=I%20just%20sold%20my%20cards%20to%20${username}!&url=https://buylist.tcgsync.com/${username}`} target="_blank" rel="noopener noreferrer" className="twitter-share" aria-label="Share on Twitter"></a>
        <a href={`https://www.tiktok.com/upload?url=https://buylist.tcgsync.com/${username}&title=I%20just%20sold%20my%20cards%20to%20${username}!`} target="_blank" rel="noopener noreferrer" className="tiktok-share" aria-label="Share on TikTok"></a>
      </div>
    </div>
  );
  setBox1Content(socialSharingContent);
};

  // Use the merchant Buylist API for every catalogue quote.
  const fetchPublicQuote = useMemo(() => createPublicBuylistPricing({
    backendUrl: BACKEND_URL, username
  }), [username]);

  const calculatePrice = useCallback(async (card, condition, printing, language = 'EN', forceRecalculate = true, collectDebugInfo = false, inventoryOffset = 0) => {
    const sealed = sealedSelection(card);
    if (sealed.sealed) {
      if (sealed.error) {
        return {
          cashPrice: 0, creditPrice: 0, basePrice: 0, isBulk: false,
          timestamp: null, contactForPrice: false, catalogIdentityError: sealed.error
        };
      }
      condition = sealed.condition;
      printing = sealed.printing;
      language = sealed.language;
    }
    const result = await fetchPublicQuote({
      productId: card.productId, condition, printing, language, forceRecalculate, inventoryOffset
    });
    if (!collectDebugInfo) return result;
    return { ...result, debugInfo: {
      cardInfo: { name: card.name, productId: card.productId, condition, printing, language },
      calculationSteps: [{ step: 'Merchant Buylist API quote', ...result }],
      finalPrices: result
    } };
  }, [fetchPublicQuote]);

  // Helper to count matching cart items for inventory-aware pricing
  // Items match when they share: productId + printing + language
  // Note: Different conditions (NM, LP, MP, HP) count TOGETHER toward the threshold
  const countMatchingCartItems = useCallback((items, productId, printing, language) => {
    return items.filter(item =>
      item.productId === productId &&
      item.printing === printing &&
      item.language === language
    ).length;
  }, []);

  const addToCart = useCallback(async (card, condition, printing, quantity, skuId, isCash, language = 'EN', orderNote = '', nonEnglish = null) => {
    const sealedIdentity = canonicalizeSealedCartItem(card);
    if (sealedIdentity.error) {
      showError(`${sealedIdentity.error} Nothing was added.`);
      return;
    }
    const sealed = sealedSelection(sealedIdentity.item);
    if (sealed.sealed) {
      condition = sealed.condition;
      printing = sealed.printing;
      language = sealed.language;
      skuId = sealed.skuId;
      card = sealedIdentity.item;
    }

    // Check if this is a custom item (which already has prices)
    if (card.isCustomItem || card.isCustom || card.isManualPrice || card.manualPrice === true) {
      console.log('Adding custom item to cart:', card);
      // For custom items, add separate line items for each quantity
      const itemsToAdd = [];
      for (let i = 0; i < quantity; i++) {
        itemsToAdd.push({
          ...card,
          quantity: 1,
          cartItemId: Date.now() + i
        });
      }
      setCartItems(prevItems => [...prevItems, ...itemsToAdd]);
      return;
    }

    // Fetch backend inventory once at start for inventory-aware pricing
    let backendInventory = 0;
    try {
      backendInventory = await fetchInventoryLevel(card.productId, printing);
      console.log(`Backend inventory for ${card.name} (${printing}): ${backendInventory}`);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      backendInventory = 0;
    }

    // Get current cart items to count existing matches
    // We need to track running count as we add items
    let currentCartItems = [];
    setCartItems(prevItems => {
      currentCartItems = [...prevItems];
      return prevItems; // Don't modify yet
    });

    // Count existing matching items in cart
    const existingMatches = countMatchingCartItems(currentCartItems, card.productId, printing, language);
    console.log(`Existing matching items in cart: ${existingMatches}`);

    // Create separate line items for each unit requested
    const itemsToAdd = [];
    const actualQuantity = Math.min(quantity, 4); // Cap at 4

    for (let i = 0; i < actualQuantity; i++) {
      // Calculate effective inventory for this specific unit
      // The offset represents "this card's position" (1-indexed), not "how many exist before"
      // So card 6 should have offset 6 to match a "6-10" rule
      const inventoryOffset = existingMatches + i + 1;

      console.log(`Adding unit ${i + 1}/${actualQuantity}: inventory offset = ${inventoryOffset} (backend: ${backendInventory}, existing: ${existingMatches}, position: ${i + 1})`);

      // Calculate price with the inventory offset
      const priceInfo = await calculatePrice(card, condition, printing, language, true, false, inventoryOffset);
      if (priceInfo.contactForPrice || priceInfo.catalogIdentityError) {
        showError(`Current pricing is unavailable for ${card.name}. Nothing was added.`);
        return;
      }
      const { cashPrice, creditPrice, isBulk, timestamp, basePrice, isDerivedPrice, derivedPercentage, inventoryModifier } = priceInfo;

      const newItem = {
        ...card,
        condition,
        printing,
        language,
        quantity: 1, // Always 1 for separate line items
        originalBasePrice: basePrice,
        basePrice,
        cashPrice,
        creditPrice,
        skuId,
        isCash,
        isBulk,
        timestamp,
        isDerivedPrice,
        derivedPercentage,
        inventoryModifier, // Store the modifier that was applied
        cartItemId: Date.now() + i, // Unique ID for each item
        orderNote,
        nonEnglish
      };

      console.log(`Item ${i + 1} prices:`, {
        name: newItem.name,
        condition: newItem.condition,
        printing: newItem.printing,
        cashPrice: newItem.cashPrice,
        creditPrice: newItem.creditPrice,
        inventoryModifier: inventoryModifier
      });

      itemsToAdd.push(newItem);
    }

    // Add all items to cart at once
    setCartItems(prevItems => [...prevItems, ...itemsToAdd]);
  }, [calculatePrice, countMatchingCartItems, fetchInventoryLevel, showError]);

  const csvImportBusy = useRef(false);
  const currentCartRef = useRef(cartItems);
  currentCartRef.current = cartItems;
  const importCartItems = useCallback(async rows => {
    if (csvImportBusy.current) throw new Error('Another CSV import is still running.');
    csvImportBusy.current = true;
    const previousCart = currentCartRef.current;
    try {
      const items = await prepareCartCsv(rows, { backendUrl: BACKEND_URL, username, enabledGames, allowedConditions, excludedSkus, calculatePrice });
      if (currentCartRef.current !== previousCart) throw new Error('Your cart changed during import. Please try again.');
      const validatedItems = items.map(item => {
        const result = canonicalizeSealedCartItem(item);
        if (result.error) throw new Error(result.error);
        return result.item;
      });
      setCartItems(validatedItems);
      return validatedItems.length;
    } finally { csvImportBusy.current = false; }
  }, [username, enabledGames, allowedConditions, excludedSkus, calculatePrice]);

  const removeFromCart = useCallback((item) => {
    if (item.cartItemId === 'all') {
      // Special case to clear the entire cart
      setCartItems([]);
      return;
    }
    setCartItems(prevItems => prevItems.filter(cartItem => cartItem.cartItemId !== item.cartItemId));
  }, []);

  // updateQuantity removed - cart now uses separate line items instead of quantity adjustment

  const togglePaymentType = useCallback((item) => {
    setCartItems(prevItems => prevItems.map(cartItem =>
      cartItem.cartItemId === item.cartItemId
        ? { ...cartItem, isCash: !cartItem.isCash }
        : cartItem
    ));
  }, []);

  const startCheckout = useCallback(() => {
    setIsCheckingOut(true);
  }, []);

  const cancelCheckout = useCallback(() => {
    setIsCheckingOut(false);
  }, []);

  const goToMainPage = useCallback(() => {
    setIsCheckingOut(false);
  }, []);

  const completeCheckout = useCallback((checkoutData) => {
    // Clear cart items
    setCartItems([]);

    // Clear local storage
    if (username) {
      const cartKey = `cartItems_${username}`;
      localStorage.removeItem(cartKey);
    }

    // Exit checkout mode
    setIsCheckingOut(false);

    console.log('Checkout completed - cart cleared, local storage removed, returned to main page');
  }, [username]);

  const clearCart = useCallback(() => {
    console.log('Clearing cart...');
    setCartItems([]);
    if (username) {
      const cartKey = `cartItems_${username}`;
      localStorage.removeItem(cartKey);
    }
    console.log('Cart cleared. New cart state:', []);
  }, [username]);

  // Save buylist functions
  const handleOpenSaveBuylistPopup = useCallback(() => {
    setShowSaveBuylistPopup(true);
  }, []);

  const handleCloseSaveBuylistPopup = useCallback(() => {
    setShowSaveBuylistPopup(false);
  }, []);

  // Load buylist functions
  const handleOpenLoadBuylistPopup = useCallback(() => {
    setShowLoadBuylistPopup(true);
  }, []);

  const handleCloseLoadBuylistPopup = useCallback(() => {
    setShowLoadBuylistPopup(false);
  }, []);

  const handleLoadBuylist = useCallback(async (savedBuylist) => {
    console.log('Loading saved buylist:', savedBuylist);

    // Clear the current cart first
    setCartItems([]);

    // Close the popup early so user sees loading progress
    setShowLoadBuylistPopup(false);

    // Recalculate prices for each item with inventory-aware logic
    const recalculatedItems = [];

    for (let i = 0; i < savedBuylist.cart_items.length; i++) {
      const identity = canonicalizeSealedCartItem(savedBuylist.cart_items[i]);
      if (identity.error) {
        showError(`${identity.error} The item was not loaded.`);
        continue;
      }
      const item = identity.item;
      if (item.isCustomItem || item.isCustom || item.isManualPrice || item.manualPrice === true) {
        recalculatedItems.push({ ...item, cartItemId: `loaded-${Date.now()}-${i}-${Math.random()}` });
        continue;
      }

      // Count matching items already added in this load batch
      const existingMatches = countMatchingCartItems(
        recalculatedItems,
        item.productId,
        item.printing,
        item.language || 'EN'
      );

      // Calculate inventory offset (1-indexed position)
      const inventoryOffset = existingMatches + 1;

      try {
        // Build a card object for calculatePrice
        const cardForPricing = {
          ...item,
          productId: item.productId,
          name: item.name,
          lowPrice: item.basePrice || item.originalBasePrice || 0,
          game: item.game,
          expansionName: item.expansionName,
          number: item.number,
          image: item.image
        };

        // Recalculate price with current settings and inventory offset
        const priceInfo = await calculatePrice(
          cardForPricing,
          item.condition,
          item.printing,
          item.language || 'EN',
          true,  // forceRecalculate
          false, // collectDebugInfo
          inventoryOffset
        );

        if (priceInfo && !priceInfo.contactForPrice && priceInfo.cashPrice !== undefined) {
          recalculatedItems.push({
            ...item,
            cashPrice: priceInfo.cashPrice,
            creditPrice: priceInfo.creditPrice,
            basePrice: priceInfo.basePrice,
            originalBasePrice: item.originalBasePrice || item.basePrice,
            cartItemId: `loaded-${Date.now()}-${i}-${Math.random()}`
          });
        } else {
          showError(`Current pricing is unavailable for ${item.name}. The item was not loaded.`);
        }
      } catch (err) {
        console.error(`Error recalculating price for ${item.name}:`, err);
        showError(`Current pricing is unavailable for ${item.name}. The item was not loaded.`);
      }
    }

    // Update the cart with the recalculated items
    setCartItems(recalculatedItems);

    // Show a success message
    const itemCount = recalculatedItems.length;
    showSuccess(`Buylist loaded successfully! Added ${itemCount} item${itemCount !== 1 ? 's' : ''}.`);
  }, [showSuccess, showError, calculatePrice, countMatchingCartItems]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleAbbrChange = useCallback((e) => {
    setSearchAbbr(e.target.value);
  }, []);

  const handleNumberChange = useCallback((e) => {
    setSearchNumber(e.target.value);
  }, []);

  const handleSearchSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSearchLoading(true);
    setIsInitialLoad(false);

    // Close the custom buylist section when performing a search
    setShowCustomItems(false);

    try {
      let translatedTerm = searchTerm;
      if (isTranslationAvailable() && i18n.language !== 'en') {
        translatedTerm = await translateText(searchTerm, i18n.language, 'en');
      } else if (!isTranslationAvailable()) {
        console.warn("Translation is not available. Using original search term.");
      }

      // Pass the raw number to backend - let backend handle special formats like "041/182" or "swsh10"
      const rawNumber = searchNumber ? searchNumber.trim() : '';

      // When performing a new search, keep the game but clear set/rarity
      setSearchParams(prev => ({
        game: prev.game || '',      // Keep selected game
        set: '',                     // Clear set/expansion for new search
        rarity: '',                  // Clear rarity filter for new search
        term: translatedTerm,
        abbr: searchAbbr.toLowerCase(),
        number: rawNumber,
        page: 1,
        sort: prev.sort || 'name-asc'
      }));
    } catch (error) {
      console.error("Error translating search term:", error);
      setSearchParams(prev => ({
        game: prev.game || '',      // Keep selected game
        set: '',                     // Clear set/expansion for new search
        rarity: '',                  // Clear rarity filter for new search
        term: searchTerm,
        abbr: searchAbbr.toLowerCase(),
        number: searchNumber ? searchNumber.trim() : '',
        page: 1,
        sort: prev.sort || 'name-asc'
      }));
    } finally {
      setIsSearchLoading(false);
    }
  }, [searchTerm, searchAbbr, searchNumber, i18n.language]);

  const handleGameChange = useCallback((e) => {
    const selectedGame = e.target.value;

    // Parse the game selection to detect sealed variants
    const { baseGame, isSealed } = parseGameSelection(selectedGame);

    // Clear all search inputs when game changes
    setSearchTerm('');
    setSearchAbbr('');
    setSearchNumber('');

    setSearchParams(prev => ({
      ...prev,
      game: baseGame,      // Use base game name for API call
      displayGame: selectedGame, // Keep original selection for UI display
      term: '',      // Clear search term when game changes
      set: '',       // Clear set when game changes
      rarity: '',    // Clear rarity when game changes
      abbr: '',      // Clear abbreviation when game changes
      number: '',    // Clear number when game changes
      page: 1,
      isSealed: isSealed  // Set sealed flag based on selection
    }));
    setIsInitialLoad(false);

    if (!selectedGame) {
      setAllExpansionNames([]);
    }
  }, [parseGameSelection]);

  const handleSetChange = useCallback((e) => {
    // Close the custom buylist section when changing set
    setShowCustomItems(false);
    
    setSearchParams(prev => ({
      ...prev,
      set: e.target.value,
      page: 1
    }));
    setIsInitialLoad(false);
  }, []);

  const handleRarityChange = useCallback((e) => {
    // Close the custom buylist section when changing rarity
    setShowCustomItems(false);

    setSearchParams(prev => ({
      ...prev,
      rarity: e.target.value,
      page: 1
    }));
    setIsInitialLoad(false);
  }, []);

  const handleSortChange = useCallback((e) => {
    // Close the custom buylist section when changing sort
    setShowCustomItems(false);

    setSearchParams(prev => ({
      ...prev,
      sort: e.target.value,
      page: 1 // Reset to page 1 when sort changes
    }));
    setIsInitialLoad(false);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    console.log('Changing to page:', newPage);
    
    // Close the custom buylist section when changing page
    setShowCustomItems(false);
    
    setSearchParams(prev => ({
      ...prev,
      page: newPage
    }));
    setIsInitialLoad(false);
  }, []);

  const handleImageRecognized = useCallback(async (productIds) => {
    setIsLoading(true);
    setIsInitialLoad(false);
    try {
      console.log("Image recognized, productIds:", productIds);
      const recognizedCards = [];
      for (const productId of productIds) {
        const cardData = await fetchCatalog({ ...searchParams, term: '', productId });
        if (cardData && cardData.items && cardData.items.length > 0) {
          recognizedCards.push(cardData.items[0]);
        }
      }
      setRecognizedCard(recognizedCards.length > 0 ? recognizedCards[0] : null);
      setCards(recognizedCards);
    } catch (error) {
      console.error("Error fetching catalog for recognized images:", error);
      setError(`Failed to fetch catalog for recognized images. Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [fetchCatalog, searchParams]);

  // Detect mobile devices and check for checkout view in URL
  useEffect(() => {
    const checkMobile = () => {
      const mobileCheck = window.innerWidth <= 768;
      const wasMobile = isMobile;
      setIsMobile(mobileCheck);

      // If mobile state changed and we have search results, refetch with new page size
      if (wasMobile !== mobileCheck && !isInitialLoad && searchParams.game) {
        // Reset to page 1 when switching between mobile/desktop to avoid pagination issues
        const newSearchParams = { ...searchParams, page: 1 };
        setSearchParams(newSearchParams);
        // Trigger refetch with new page size
        setTimeout(() => {
          fetchCatalog(newSearchParams);
        }, 0);
      }
    };

    // Handle messages from checkout iframe
    const handleMessage = (event) => {
      if (event.data.type === 'RESET_APP' || event.data.type === 'ORDER_COMPLETED') {
        // Clear cart and local storage
        setCartItems([]);
        if (username) {
          const cartKey = `cartItems_${username}`;
          localStorage.removeItem(cartKey);
        }
        // Exit checkout mode
        setIsCheckingOut(false);
        console.log('App reset after order completion - cart cleared and local storage removed');
      } else if (event.data.type === 'CLEAR_CART') {
        // Clear cart and local storage
        setCartItems([]);
        if (username) {
          const cartKey = `cartItems_${username}`;
          localStorage.removeItem(cartKey);
        }
        console.log('Cart cleared from checkout');
      }
    };

    // Check on initial load
    checkMobile();

    // Check for view=checkout in URL
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    if (view === 'checkout') {
      setIsCheckingOut(true);
    }

    // Add event listeners
    window.addEventListener('resize', checkMobile);
    window.addEventListener('message', handleMessage);

    // Clean up
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('message', handleMessage);
    };
  }, [username, isMobile, isInitialLoad, searchParams, fetchCatalog, setSearchParams]);

  // Check user settings for template preference
  useEffect(() => {
    if (userSettings) {
      setUseAlternativeUI(userSettings.use_v2_template === true);
    }
  }, [userSettings]);

  // Calculate if cash limit has been reached
  // Note: quantity is always 1 now (separate line items)
  const isCashLimitReached = useMemo(() => {
    if (!userSettings || !userSettings.cash_limit) return false;

    const cashSubtotal = cartItems.reduce((sum, item) =>
      sum + (item.isCash ? parseFloat(item.cashPrice) : 0), 0);

    return cashSubtotal >= userSettings.cash_limit;
  }, [cartItems, userSettings]);

  const memoizedCardList = useMemo(() => (
    <CardList
      cards={cards}
      addToCart={addToCart}
      allowedConditions={allowedConditions}
      floorPrice={floorPrice}
      bulkSettings={bulkSettings}
      userSettings={userSettings}
      excludedSku={excludedSkus}
      calculatePrice={calculatePrice}
      exchangeRates={exchangeRates}
      isCashLimitReached={isCashLimitReached}
      hotlistItems={hotlistItems}
      hotlistCashPercent={hotlistCashPercent}
      hotlistCreditPercent={hotlistCreditPercent}
      hotlistFixedPrices={hotlistFixedPrices}
      itemsPerPage={itemsPerPage}
      setItemsPerPage={setItemsPerPage}
      cartItems={cartItems}
    />
  ), [cards, addToCart, allowedConditions, floorPrice, bulkSettings, userSettings, excludedSkus, calculatePrice, exchangeRates, isCashLimitReached, hotlistItems, hotlistCashPercent, hotlistCreditPercent, hotlistFixedPrices, itemsPerPage, cartItems]);

  if (!userSettings && !error) {
    return <div className="loading-screen" role="status">Loading buylist…</div>;
  }

  if (error) {
    return <div>{t('Error')}: {error}</div>;
  }

  if (isCheckingOut) {
    // Check if dark theme should be used (same condition as main UI)
    const useDarkTheme = username?.toLowerCase() === 'smokeandmirrors' || userSettings?.use_v3_template === true;
    const checkoutContent = (
      <>
        <CheckoutPage
          cartItems={cartItems}
          removeFromCart={removeFromCart}
          completeCheckout={completeCheckout}
          cancelCheckout={cancelCheckout}
          goToMainPage={goToMainPage}
          userSettings={userSettings}
          username={username}
          calculatePrice={calculatePrice}
          togglePaymentType={togglePaymentType}
          paymentSplitData={priceSplitEnabled ? paymentSplitData : null}
          onSaveForLater={handleOpenSaveBuylistPopup}
          onLoadBuylist={handleOpenLoadBuylistPopup}
          setCartItems={setCartItems}
        />
        {showSaveBuylistPopup && (
          <SaveBuylistPopup
            onClose={handleCloseSaveBuylistPopup}
            cartItems={cartItems}
            username={username}
            userSettings={userSettings}
          />
        )}
        {showLoadBuylistPopup && (
          <LoadBuylistPopup
            onClose={handleCloseLoadBuylistPopup}
            onLoadBuylist={handleLoadBuylist}
            username={username}
            userSettings={userSettings}
          />
        )}
      </>
    );

    if (userSettings?.use_simplistic_template === true || userSettings?.template_version === 'simplistic') {
      return <div className="theme-simplistic simplistic-checkout" data-simple-theme={simplisticTheme}>{checkoutContent}</div>;
    }

    if (userSettings?.use_orbit_template === true || userSettings?.template_version === 'orbit') {
      return <div className="theme-orbit orbit-checkout">{checkoutContent}</div>;
    }

    // Wrap in theme container for dark theme users
    if (useDarkTheme) {
      return <div className="theme-smokeandmirrors">{checkoutContent}</div>;
    }
    return checkoutContent;
  }

  // Check if smokeandmirrors theme - skip mobile app for this theme
  const isSmokeandMirrors = username?.toLowerCase() === 'smokeandmirrors' || userSettings?.use_v3_template === true;

  // For smokeandmirrors theme, always use AlternativeAppDark even on mobile
  const isOrbit = userSettings?.use_orbit_template === true || userSettings?.template_version === 'orbit';
  const TemplateApp = isOrbit ? OrbitApp : SimplisticApp;
  const isSimplistic = userSettings?.use_simplistic_template === true || userSettings?.template_version === 'simplistic';
  const shouldUseMobileApp = isMobile && !isSmokeandMirrors && !isSimplistic && !isOrbit;

  // Block UI if subscription is not valid
  if (subscriptionBlocked) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '24px', color: '#333', marginBottom: '16px' }}>
          This buylist is currently unavailable
        </h1>
        <p style={{ fontSize: '16px', color: '#666' }}>
          {subscriptionMessage || 'Please contact admin@tcgsync.com for assistance.'}
        </p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      {isOrbit || isSimplistic ? (
        <TemplateApp
          onLoadBuylist={handleLoadBuylist}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          enabledGames={enabledGames}
          cartItems={cartItems}
          setCartItems={setCartItems}
          importCartItems={importCartItems}
          removeFromCart={removeFromCart}
          checkout={startCheckout}
          togglePaymentType={togglePaymentType}
          clearCart={clearCart}
          userSettings={userSettings}
          cards={cards}
          addToCart={addToCart}
          allowedConditions={allowedConditions}
          floorPrice={floorPrice}
          bulkSettings={bulkSettings}
          excludedSku={excludedSkus}
          calculatePrice={calculatePrice}
          exchangeRates={exchangeRates}
          handleGameChange={handleGameChange}
          handleSetChange={handleSetChange}
          handleSortChange={handleSortChange}
          searchParams={{
            ...searchParams,
            allExpansionNames: allExpansionNames,
            allRarities: allRarities,
            totalPages: totalPages
          }}
          setSearchParams={setSearchParams}
          isLoading={isLoading}
          isInitialLoad={isInitialLoad}
          setIsInitialLoad={setIsInitialLoad}
          customItems={customItems}
          fetchCustomItems={fetchCustomItems}
          hotlistItems={hotlistItems}
          hotlistCashPercent={hotlistCashPercent}
          hotlistCreditPercent={hotlistCreditPercent}
          hotlistFixedPrices={hotlistFixedPrices}
          fetchHotlistItems={fetchHotlistItems}
          additionalBuylistCategories={additionalBuylistCategories}
          additionalBuylistItems={additionalBuylistItems}
          fetchAdditionalBuylistItems={fetchAdditionalBuylistItems}
          selectedAdditionalCategory={selectedAdditionalCategory}
          username={username}
          showGradedCardsButton={showGradedCardsButton}
          setShowGradedCardsPopup={setShowGradedCardsPopup}
          paymentSplitData={priceSplitEnabled ? paymentSplitData : null}
          setPaymentSplitData={effectiveSetPaymentSplitData}
          gameSettingsV2={gameSettingsV2}
        />
      ) : shouldUseMobileApp ? (
        <MobileApp
          enabledGames={enabledGames}
          cartItems={cartItems}
          setCartItems={setCartItems}
          removeFromCart={removeFromCart}
          checkout={startCheckout}
          togglePaymentType={togglePaymentType}
          clearCart={clearCart}
          userSettings={userSettings}
          cards={cards}
          addToCart={addToCart}
          allowedConditions={allowedConditions}
          floorPrice={floorPrice}
          bulkSettings={bulkSettings}
          excludedSku={excludedSkus}
          calculatePrice={calculatePrice}
          exchangeRates={exchangeRates}
          handleGameChange={handleGameChange}
          handleSetChange={handleSetChange}
          searchParams={{
            ...searchParams,
            allExpansionNames: allExpansionNames,
            allRarities: allRarities,
            totalPages: totalPages
          }}
          setSearchParams={setSearchParams}
          isLoading={isLoading}
          isInitialLoad={isInitialLoad}
          setIsInitialLoad={setIsInitialLoad}
          customItems={customItems}
          fetchCustomItems={fetchCustomItems}
          hotlistItems={hotlistItems}
          hotlistFixedPrices={hotlistFixedPrices}
          fetchHotlistItems={fetchHotlistItems}
          additionalBuylistCategories={additionalBuylistCategories}
          additionalBuylistItems={additionalBuylistItems}
          fetchAdditionalBuylistItems={fetchAdditionalBuylistItems}
          selectedAdditionalCategory={selectedAdditionalCategory}
          username={username}
          showGradedCardsButton={showGradedCardsButton}
          setShowGradedCardsPopup={setShowGradedCardsPopup}
          paymentSplitData={priceSplitEnabled ? paymentSplitData : null}
          setPaymentSplitData={effectiveSetPaymentSplitData}
          onLoadBuylist={handleLoadBuylist}
        />
      ) : username?.toLowerCase() === 'smokeandmirrors' || userSettings?.use_v3_template === true ? (
        <AlternativeAppDark
          enabledGames={enabledGames}
          cartItems={cartItems}
          setCartItems={setCartItems}
          importCartItems={importCartItems}
          removeFromCart={removeFromCart}
          checkout={startCheckout}
          togglePaymentType={togglePaymentType}
          clearCart={clearCart}
          userSettings={userSettings}
          cards={cards}
          addToCart={addToCart}
          allowedConditions={allowedConditions}
          floorPrice={floorPrice}
          bulkSettings={bulkSettings}
          excludedSku={excludedSkus}
          calculatePrice={calculatePrice}
          exchangeRates={exchangeRates}
          handleGameChange={handleGameChange}
          handleSetChange={handleSetChange}
          handleSortChange={handleSortChange}
          searchParams={{
            ...searchParams,
            allExpansionNames: allExpansionNames,
            allRarities: allRarities,
            totalPages: totalPages
          }}
          setSearchParams={setSearchParams}
          isLoading={isLoading}
          isInitialLoad={isInitialLoad}
          setIsInitialLoad={setIsInitialLoad}
          customItems={customItems}
          fetchCustomItems={fetchCustomItems}
          hotlistItems={hotlistItems}
          hotlistCashPercent={hotlistCashPercent}
          hotlistCreditPercent={hotlistCreditPercent}
          hotlistFixedPrices={hotlistFixedPrices}
          fetchHotlistItems={fetchHotlistItems}
          additionalBuylistCategories={additionalBuylistCategories}
          additionalBuylistItems={additionalBuylistItems}
          fetchAdditionalBuylistItems={fetchAdditionalBuylistItems}
          selectedAdditionalCategory={selectedAdditionalCategory}
          username={username}
          showGradedCardsButton={showGradedCardsButton}
          setShowGradedCardsPopup={setShowGradedCardsPopup}
          paymentSplitData={priceSplitEnabled ? paymentSplitData : null}
          setPaymentSplitData={effectiveSetPaymentSplitData}
          gameSettingsV2={gameSettingsV2}
        />
      ) : useAlternativeUI ? (
        <AlternativeApp
          enabledGames={enabledGames}
          cartItems={cartItems}
          setCartItems={setCartItems}
          importCartItems={importCartItems}
          removeFromCart={removeFromCart}
          checkout={startCheckout}
          togglePaymentType={togglePaymentType}
          clearCart={clearCart}
          userSettings={userSettings}
          cards={cards}
          addToCart={addToCart}
          allowedConditions={allowedConditions}
          floorPrice={floorPrice}
          bulkSettings={bulkSettings}
          excludedSku={excludedSkus}
          calculatePrice={calculatePrice}
          exchangeRates={exchangeRates}
          handleGameChange={handleGameChange}
          handleSetChange={handleSetChange}
          searchParams={{
            ...searchParams,
            allExpansionNames: allExpansionNames,
            allRarities: allRarities,
            totalPages: totalPages
          }}
          setSearchParams={setSearchParams}
          isLoading={isLoading}
          isInitialLoad={isInitialLoad}
          setIsInitialLoad={setIsInitialLoad}
          customItems={customItems}
          fetchCustomItems={fetchCustomItems}
          hotlistItems={hotlistItems}
          hotlistCashPercent={hotlistCashPercent}
          hotlistCreditPercent={hotlistCreditPercent}
          hotlistFixedPrices={hotlistFixedPrices}
          fetchHotlistItems={fetchHotlistItems}
          additionalBuylistCategories={additionalBuylistCategories}
          additionalBuylistItems={additionalBuylistItems}
          fetchAdditionalBuylistItems={fetchAdditionalBuylistItems}
          selectedAdditionalCategory={selectedAdditionalCategory}
          username={username}
          showGradedCardsButton={showGradedCardsButton}
          setShowGradedCardsPopup={setShowGradedCardsPopup}
          paymentSplitData={priceSplitEnabled ? paymentSplitData : null}
          setPaymentSplitData={effectiveSetPaymentSplitData}
          gameSettingsV2={gameSettingsV2}
        />
      ) : (
        <div className="app">
          {showEnquiryPopup && (
            <EnquiryPopup
              onClose={handleCloseEnquiryPopup}
              onContinue={handleContinueEnquiry}
            />
          )}
          {showSaveBuylistPopup && (
            <SaveBuylistPopup
              onClose={handleCloseSaveBuylistPopup}
              cartItems={cartItems}
              username={username}
              userSettings={userSettings}
            />
          )}
          {showLoadBuylistPopup && (
            <LoadBuylistPopup
              onClose={handleCloseLoadBuylistPopup}
              onLoadBuylist={handleLoadBuylist}
              username={username}
              userSettings={userSettings}
            />
          )}
          {showGradedCardsPopup && (
            <GradedCardsPopup
              onClose={() => setShowGradedCardsPopup(false)}
              username={username}
              userSettings={userSettings}
            />
          )}
          {/* Price change notification removed as we're keeping original prices */}
          {/* Left sidebar removed */}
          <div className="main-content">
            <div className="filters">
            <div className="search-and-upload">
              <div className="search-mode-toggle">
                <button
                  className={`search-mode-button ${!showBulkSearch && !showCardRecognition ? 'active' : ''}`}
                  onClick={() => {
                    setShowBulkSearch(false);
                    setShowCardRecognition(false);
                    setShowCustomItems(false);
                    setShowAdditionalBuylist(false);
                  }}
                >
                  {t('Single Search')}
                </button>
                <button
                  className={`search-mode-button ${showBulkSearch ? 'active' : ''}`}
                  onClick={() => {
                    setShowBulkSearch(true);
                    setShowCardRecognition(false);
                    setShowCustomItems(false);
                    setShowAdditionalBuylist(false);
                  }}
                >
                  {t('Bulk Search')}
                </button>
                <button
                  className={`search-mode-button ${showCardRecognition ? 'active' : ''}`}
                  onClick={() => {
                    setShowCardRecognition(true);
                    setShowBulkSearch(false);
                    setShowCustomItems(false);
                    setShowAdditionalBuylist(false);
                  }}
                >
                  {t('Scan My Cards')}
                </button>
                <button 
                  className="search-mode-button load-buylist-button-main"
                  onClick={handleOpenLoadBuylistPopup}
                >
                  {t('Load Buylist')}
                </button>
                <button
                  className="search-mode-button hotlist-button-main"
                  onClick={() => {
                    // Reset to start page and load hotlist items
                    setShowBulkSearch(false);
                    setShowCardRecognition(false);
                    setShowCustomItems(false);
                    setShowAdditionalBuylist(false);
                    setIsInitialLoad(true);
                    setSearchParams({
                      term: '',
                      abbr: '',
                      number: '',
                      game: '',
                      set: '',
                      rarity: '',
                      page: 1,
                      sort: 'name-asc'
                    });
                    setSearchTerm('');
                    setSearchAbbr('');
                    setSearchNumber('');
                    setCards([]);
                    // Fetch hotlist items which will populate the cards
                    fetchHotlistItems(username);
                  }}
                >
                  {t('Hotlist')}
                </button>
                {customItems.length > 0 && (
                  <button
                    className="search-mode-button custom-buylist-button-main"
                    onClick={() => {
                      setShowCustomItems(!showCustomItems);
                      if (!showCustomItems) {
                        setShowAdditionalBuylist(false);
                        setShowBulkSearch(false);
                        setShowCardRecognition(false);
                      }
                    }}
                  >
                    {showCustomItems ? t('Hide Custom Items') : (userSettings?.custom_buylist_button_label || t('We Also Buy'))}
                  </button>
                )}
                {additionalBuylistCategories && additionalBuylistCategories.map((category, index) => (
                  <button
                    key={index}
                    className="search-mode-button additional-buylist-button-main"
                    onClick={() => {
                      setShowAdditionalBuylist(true);
                      setShowCustomItems(false);
                      setShowBulkSearch(false);
                      setShowCardRecognition(false);
                      setSelectedAdditionalCategory(category);
                      fetchAdditionalBuylistItems(username, category.product_type);
                    }}
                  >
                    {category.button_label}
                  </button>
                ))}
                {showGradedCardsButton && (
                  <button
                    className="search-mode-button graded-cards-button-main"
                    onClick={() => setShowGradedCardsPopup(true)}
                  >
                    {t('Graded Cards')}
                  </button>
                )}
              </div>
              {!showBulkSearch && !showCardRecognition ? (
                <div className="unified-search-container">
                  <form onSubmit={handleSearchSubmit} className="unified-search-form">
                    <div className="search-fields-row">
                      <select className="form-field" value={searchParams.game} onChange={handleGameChange}>
                        <option value="">{t('Please select a game')}</option>
                        {enabledGames.map((game, index) => (
                          <option key={index} value={game}>{game}</option>
                        ))}
                      </select>
                      <select 
                        className="form-field" 
                        value={searchParams.set} 
                        onChange={handleSetChange}
                        disabled={!searchParams.game}
                      >
                        <option value="">{t('All Sets')}</option>
                        {allExpansionNames.map((set, index) => (
                          <option key={index} value={set}>{set}</option>
                        ))}
                      </select>
                      <select 
                        className="form-field" 
                        value={searchParams.rarity} 
                        onChange={handleRarityChange}
                        disabled={!searchParams.game}
                      >
                        <option value="">{t('All Rarities')}</option>
                        {allRarities.map((rarity, index) => (
                          <option key={index} value={rarity}>{rarity}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder={t('Search by Name')}
                        className="form-field"
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <input
                        type="text"
                        placeholder={t('Search by Abbreviation')}
                        className="form-field"
                        value={searchAbbr}
                        onChange={handleAbbrChange}
                      />
                      <input
                        type="text"
                        placeholder={t('Search by Number (e.g., 041/182, swsh10)')}
                        className="form-field"
                        value={searchNumber}
                        onChange={handleNumberChange}
                      />
                      <button type="submit" className="search-button" disabled={!searchParams.game}>
                        {searchParams.game ? t('Search') : t('Select a game first')}
                      </button>
                      <button
                        type="button"
                        className="cart-toggle-button"
                        onClick={() => setIsCartVisible(!isCartVisible)}
                      >
                        {isCartVisible ? t('Hide Cart') : t('Show Cart')} ({cartItems.length})
                      </button>
                    </div>
                  </form>
                </div>
              ) : null}
            </div>
              {!isTranslationAvailable() && (
                <p className="translation-warning">
                  {t('Translation is not available. Searches will be performed with the original text.')}
                </p>
              )}
            </div>
            {searchError && (
              <div className="search-error">
                <p>{searchError}</p>
              </div>
            )}
            
            {/* Hotlist Items Section */}
            {/* Hotlist section removed as requested */}

            {/* Main content area - conditionally render different views */}
            {isSearchLoading ? (
              <div className="loading-screen">
                <p>{t('Just fetching you the best prices...')}</p>
              </div>
            ) : isLoading ? (
              <div>{t('Loading')}...</div>
            ) : (
              <>
                {/* Custom Buylist Items Table */}
                {showCustomItems && customItems.length > 0 ? (
              <div className="custom-buylist-container main-custom-buylist">
                <h3>Custom Items We Buy</h3>
                <table className="custom-buylist-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Description</th>
                      <th>Cash Price</th>
                      <th>Credit Price</th>
                      <th>Quantity</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customItems.map((item) => (
                      <tr key={item._id.$oid}>
                        <td>{item.title}</td>
                        <td>{item.description}</td>
                        <td>{currencySymbols[userCurrency] || '$'}{item.cash_price}</td>
                        <td>{currencySymbols[userCurrency] || '$'}{item.credit_price}</td>
                        <td>
                          <input 
                            type="number" 
                            min="1" 
                            max="999" 
                            defaultValue="1"
                            className="custom-item-quantity"
                          />
                        </td>
                        <td>
                          <button 
                            className="add-custom-item-cash"
                            onClick={(e) => {
                              const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.custom-item-quantity').value);
                              const customItem = {
                                name: item.title,
                                description: item.description,
                                cashPrice: item.cash_price,
                                creditPrice: item.credit_price,
                                quantity: quantity,
                                isCash: true,
                                cartItemId: `custom-${item._id.$oid}-${Date.now()}`,
                                condition: 'N/A',
                                printing: 'N/A',
                                isCustomItem: true
                              };
                              setCartItems(prevItems => [...prevItems, customItem]);
                            }}
                          >
                            Add (Cash)
                          </button>
                          <button 
                            className="add-custom-item-credit"
                            onClick={(e) => {
                              const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.custom-item-quantity').value);
                              const customItem = {
                                name: item.title,
                                description: item.description,
                                cashPrice: item.cash_price,
                                creditPrice: item.credit_price,
                                quantity: quantity,
                                isCash: false,
                                cartItemId: `custom-${item._id.$oid}-${Date.now()}`,
                                condition: 'N/A',
                                printing: 'N/A',
                                isCustomItem: true
                              };
                              setCartItems(prevItems => [...prevItems, customItem]);
                            }}
                          >
                            Add (Credit)
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                ) : showAdditionalBuylist && additionalBuylistItems.length > 0 ? (
                  <div className="custom-buylist-container main-custom-buylist">
                    <h3>{selectedAdditionalCategory?.button_label || 'Additional Items'}</h3>
                    {selectedAdditionalCategory?.description && (
                      <div className="additional-buylist-description">
                        <p>{selectedAdditionalCategory.description}</p>
                      </div>
                    )}
                <table className="custom-buylist-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Product</th>
                      <th>Cash Price</th>
                      <th>Credit Price</th>
                      <th>Quantity</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {additionalBuylistItems.map((product) => (
                      product.variants
                        .filter(variant => {
                          const cashPrice = parseFloat(variant.cashPrice) || 0;
                          const creditPrice = parseFloat(variant.creditPrice) || 0;
                          return cashPrice > 0 || creditPrice > 0;
                        })
                        .map((variant, variantIndex) => (
                        <tr key={`${product.productId}-${variantIndex}`}>
                          <td>
                            {(variant.image || product.image) && (
                              <img
                                src={variant.image || product.image}
                                alt={product.title}
                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                              />
                            )}
                          </td>
                          <td>{product.title}{variant.title !== 'Default Title' && ` - ${variant.title}`}</td>
                          <td>{currencySymbols[userCurrency] || '$'}{variant.cashPrice}</td>
                          <td>{currencySymbols[userCurrency] || '$'}{variant.creditPrice}</td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              max="999"
                              defaultValue="1"
                              className="custom-item-quantity"
                            />
                          </td>
                          <td>
                            <button
                              className="add-custom-item-cash"
                              onClick={(e) => {
                                const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.custom-item-quantity').value);
                                const basePrice = parseFloat(variant.price);
                                const additionalItem = {
                                  name: `${product.title} - ${variant.title}`,
                                  cashPrice: parseFloat(variant.cashPrice),
                                  creditPrice: parseFloat(variant.creditPrice),
                                  quantity: quantity,
                                  isCash: true,
                                  cartItemId: `additional-${product.productId}-${variantIndex}-${Date.now()}`,
                                  condition: 'N/A',
                                  printing: 'N/A',
                                  isAdditionalBuylist: true,
                                  productType: selectedAdditionalCategory?.product_type,
                                  basePrice: basePrice,
                                  originalBasePrice: basePrice
                                };
                                setCartItems(prevItems => [...prevItems, additionalItem]);
                              }}
                            >
                              Add (Cash)
                            </button>
                            <button
                              className="add-custom-item-credit"
                              onClick={(e) => {
                                const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.custom-item-quantity').value);
                                const basePrice = parseFloat(variant.price);
                                const additionalItem = {
                                  name: `${product.title} - ${variant.title}`,
                                  cashPrice: parseFloat(variant.cashPrice),
                                  creditPrice: parseFloat(variant.creditPrice),
                                  quantity: quantity,
                                  isCash: false,
                                  cartItemId: `additional-${product.productId}-${variantIndex}-${Date.now()}`,
                                  condition: 'N/A',
                                  printing: 'N/A',
                                  isAdditionalBuylist: true,
                                  productType: selectedAdditionalCategory?.product_type,
                                  basePrice: basePrice,
                                  originalBasePrice: basePrice
                                };
                                setCartItems(prevItems => [...prevItems, additionalItem]);
                              }}
                            >
                              Add (Credit)
                            </button>
                          </td>
                        </tr>
                      ))
                    ))}
                  </tbody>
                </table>
              </div>
                ) : showBulkSearch ? (
                  <BulkSearchPage
                    addToCart={addToCart}
                    userSettings={userSettings}
                    allowedConditions={allowedConditions}
                    calculatePrice={calculatePrice}
                    exchangeRates={exchangeRates}
                    hotlistItems={hotlistItems}
                    hotlistCashPercent={hotlistCashPercent}
                    hotlistCreditPercent={hotlistCreditPercent}
                    hotlistFixedPrices={hotlistFixedPrices}
                  />
                ) : showCardRecognition ? (
                  <CardRecognitionPage
                    addToCart={addToCart}
                    userSettings={userSettings}
                    exchangeRates={exchangeRates}
                    calculatePrice={calculatePrice}
                    allowedConditions={allowedConditions}
                    onRecognizedCard={(recognizedCards) => {
                      console.log('Cards recognized:', recognizedCards);
                      if (recognizedCards.length > 0) {
                        const productId = recognizedCards[0].productId;
                        console.log('Triggering search for productId:', productId);

                        // Use EXACT same process as manual search
                        // First try Magic: The Gathering
                        const searchForCard = async (game) => {
                          try {
                            const url = new URL(`${BACKEND_URL}/catalog`);
                            // For v3 template (use_v2_template), default to false except for MinMaxGames
                            const isV3Template = userSettings?.use_v3_template === true;
                            const isMinMaxGames = username?.toLowerCase() === 'minmaxgames';
                            const defaultShowValue = isV3Template && !isMinMaxGames ? false : true;

                            const showNotBuying = localStorage.getItem('showNotBuying') !== null ?
                              JSON.parse(localStorage.getItem('showNotBuying')) : defaultShowValue;
                            const showBulkItems = localStorage.getItem('showBulkItems') !== null ?
                              JSON.parse(localStorage.getItem('showBulkItems')) : defaultShowValue;

                            // Use different page sizes for mobile vs desktop
                            const pageLimit = isMobile ? '12' : '25';

                            const searchParams = {
                              game: game,
                              page: '1',
                              limit: pageLimit,
                              productId: productId.toString(),
                              username: username,
                              showBulk: showBulkItems.toString(),
                              showNotBuying: showNotBuying.toString()
                            };
                            
                            url.search = new URLSearchParams(searchParams).toString();
                            console.log('Card recognition fetching from catalog URL:', url.toString());

                            const response = await fetch(url);
                            if (!response.ok) {
                              throw new Error(`HTTP error! status: ${response.status}`);
                            }

                            const data = await response.json();
                            console.log('Card recognition received catalog data:', data);

                            if (data.items && data.items.length > 0) {
                              // Found the card! Use EXACT same process as manual search
                              setCards(data.items);
                              setTotalPages(data.totalPages || 1);
                              
                              // Update expansions and rarities like manual search does
                              const expansions = data.expansionNames || [];
                              setAllExpansionNames(expansions);
                              const rarities = data.rarities || [];
                              setAllRarities(rarities);

                              // Set search params to match what was found
                              setSearchParams({
                                term: '',
                                abbr: '',
                                number: '',
                                game: game,
                                set: '',
                                rarity: '',
                                page: 1,
                                sort: 'name-asc'
                              });

                              // Switch back to normal view to show the search results
                              setShowCardRecognition(false);
                              setIsInitialLoad(false);
                              
                              console.log('Card recognition completed - found card in', game);
                              return true; // Found the card
                            }
                            return false; // Card not found in this game
                          } catch (error) {
                            console.error(`Error searching for card in ${game}:`, error);
                            return false;
                          }
                        };

                        // Try different games in order
                        const tryGames = async () => {
                          const gamesToTry = ['Magic: The Gathering', 'Pokemon', 'Yu-Gi-Oh!', 'Lorcana'];
                          
                          for (const game of gamesToTry) {
                            console.log(`Card recognition trying game: ${game}`);
                            const found = await searchForCard(game);
                            if (found) {
                              return; // Found it, stop searching
                            }
                          }
                          
                          // If we get here, card wasn't found in any game
                          console.log('Card recognition: Card not found in any game');
                          setError('Card not found in our catalog. Please try again.');
                          setShowCardRecognition(false);
                        };

                        tryGames();
                      }
                    }}
                  />
                ) : (
                  userSettings && memoizedCardList
                )}
                {!isInitialLoad && (
                  <div className="pagination">
                    {cards.length > 0 && (
                      <>
                        {totalPages > 1 ? (
                          <>
                            <button onClick={() => handlePageChange(searchParams.page - 1)} disabled={searchParams.page === 1}>{t('Previous')}</button>
                            <span>{t('Page')} {searchParams.page} {t('of')} {totalPages}</span>
                            <button onClick={() => handlePageChange(searchParams.page + 1)} disabled={searchParams.page === totalPages}>{t('Next')}</button>
                          </>
                        ) : (
                          <span className="showing-all-results">
                            {t('Showing all')} {cards.length} {t('results')}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className={`cart-container ${isCartVisible ? 'visible' : 'hidden'}`}>
            <AlternativeCart
              cartItems={cartItems}
              importCartItems={importCartItems}
              removeFromCart={removeFromCart}
              checkout={startCheckout}
              togglePaymentType={togglePaymentType}
              userSettings={userSettings}
              clearCart={clearCart}
              onSaveForLater={handleOpenSaveBuylistPopup}
              onLoadBuylist={handleOpenLoadBuylistPopup}
              calculatePrice={calculatePrice}
              allowedConditions={allowedConditions}
              setCartItems={setCartItems}
              customItems={customItems}
              isLoadingCustomItems={false}
              paymentSplitData={priceSplitEnabled ? paymentSplitData : null}
              setPaymentSplitData={effectiveSetPaymentSplitData}
            />
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
};

export default App;
